// src/services/buddyXApiHubService.js
import axios from "axios";
import finalConfig from "configs/config";
import { trackAction, checkBalanceBeforeAction, authAPI } from "./transactionService";

const API_URL = finalConfig.BUDDYX_API_BASE_URL;

const axiosInstance = axios.create({
  baseURL: API_URL,
});

// WSO2 Token Management Functions
const getWSO2Token = () => {
  // Get WSO2 token from authAPI
  return authAPI.getIdToken();
};

const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp && payload.exp < currentTime;
  } catch (error) {
    console.error("Error checking token expiration:", error);
    return true;
  }
};

// Add request interceptor for WSO2 token authentication
axiosInstance.interceptors.request.use(
  (config) => {
    const token = getWSO2Token();
    
    // Check if we have a valid WSO2 token
    if (!token) {
      return Promise.reject(new Error('No WSO2 token available'));
    }
    
    // Check if token is expired
    if (isTokenExpired(token)) {
      console.warn('WSO2 token is expired, attempting to refresh...');
      // Trigger logout if token is expired
      if (window.logoutUser) {
        window.logoutUser();
      } else {
        window.location.href = '/sign-in';
      }
      return Promise.reject(new Error('WSO2 token expired'));
    }
    
    // Add WSO2 token to Authorization header
    config.headers['Authorization'] = `Bearer ${token}`;
    config.headers['Content-Type'] = 'application/json';
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for WSO2 token handling
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle WSO2 token validation errors
    if (error.response?.status === 401 && error.response?.data?.error_code === 'WSO2_VALIDATION_FAILED') {
      console.error('WSO2 token validation failed - clearing tokens');
      authAPI.clearTokens();
      // Redirect to login or show error
      if (window.logoutUser) {
        window.logoutUser();
      } else {
        window.location.href = '/sign-in';
      }
    }
    return Promise.reject(error);
  }
);

// Add automatic cleanup on page unload
window.addEventListener('beforeunload', () => {
  // Clear tokens when user leaves the page
  authAPI.clearTokens();
});

// Add cleanup on visibility change (tab switching)
document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    // Optional: Clear tokens when tab is hidden for extra security
    // authAPI.clearTokens(); // Uncomment for maximum security
  }
});

// Export security utilities for external use
export const securityUtils = {
  clearTokens: authAPI.clearTokens,
  getToken: authAPI.getIdToken,
  forceLogout: () => {
    authAPI.clearTokens();
    if (window.logoutUser) {
      window.logoutUser();
    } else {
      window.location.href = '/sign-in';
    }
  }
};

// Helper function to check balance and show popup if needed
const checkBalanceAndProceed = async (actionType, onInsufficientCredits) => {
  try {
    const balanceCheck = await checkBalanceBeforeAction(actionType);
    
    if (!balanceCheck.hasEnoughCredits) {
      // Call the callback to show insufficient credits popup
      if (onInsufficientCredits && typeof onInsufficientCredits === 'function') {
        onInsufficientCredits(balanceCheck);
        return { canProceed: false, updateBalance: null }; // Don't proceed with the action
      } else {
        // Show default error if no callback provided
        const errorMessage = `Insufficient credits! You need ${balanceCheck.requiredCredits} credits for ${balanceCheck.actionName}, but you only have ${balanceCheck.currentBalance} credits.`;
        throw new Error(errorMessage);
      }
    }
    
    // Return a function to update balance after successful operation
    const updateBalance = async () => {
      try {
        // Get the current balance from the global window object if available
        if (window.updateUserBalance && typeof window.updateUserBalance === 'function') {
          await window.updateUserBalance();
        }
      } catch (error) {
        console.error("Error updating balance:", error);
      }
    };
    
    return { canProceed: true, updateBalance }; // Proceed with the action
  } catch (error) {
    console.error("Error checking balance:", error);
    throw error; // Re-throw the error so it can be caught by the calling function
  }
};

const get_seo_title_for_single_keyword = async (data, onInsufficientCredits = null) => {
  try {
    // Check balance before proceeding
    const { canProceed, updateBalance } = await checkBalanceAndProceed("seo_title_single_keyword", onInsufficientCredits);
    if (!canProceed) {
      return;
    }
  
    const response = await axiosInstance.post(
      "/llm/seo/title/single_keyword",
      data
    );

    // Track the action after successful API call
    await trackAction(
      "seo_title_single_keyword", 
      `SEO title generation for keyword: ${data.keyword}`,
      data,
      response.data.data.results
    );

    // Update balance after successful operation
    if (updateBalance) {
      await updateBalance();
    }

    return response.data.data.results;
  } catch (error) {
    console.error("Error posting data", error);
    throw error;
  }
};

const get_seo_title_for_multiple_keywords = async (data, onInsufficientCredits = null) => {
  try {
    // Check balance before proceeding
    const { canProceed, updateBalance } = await checkBalanceAndProceed("seo_title_multiple_keywords", onInsufficientCredits);
    if (!canProceed) {
      return;
    }
    const response = await axiosInstance.post(
      "/llm/seo/title/multiple_keywords",
      data
    );

    // Track the action after successful API call
    await trackAction(
      "seo_title_multiple_keywords", 
      `SEO title generation for multiple keywords: ${data.keywords.join(', ')}`,
      data,
      response.data.data.results
    );

    // Update balance after successful operation
    if (updateBalance) {
      await updateBalance();
    }

    return response.data.data.results;
  } catch (error) {
    console.error("Error posting data", error);
    throw error;
  }
};

const get_seo_meta_description_for_single_keyword = async (data, onInsufficientCredits = null) => {
  try {
    // Check balance before proceeding
    const { canProceed, updateBalance } = await checkBalanceAndProceed("seo_meta_description_single_keyword", onInsufficientCredits);
    if (!canProceed) {
      return;
    }
  
    const response = await axiosInstance.post(
      "/llm/seo/meta_description/single_keyword",
      data
    );

    // Track the action after successful API call
    await trackAction(
      "seo_meta_description_single_keyword", 
      `SEO meta description for keyword: ${data.keyword}`,
      data,
      response.data.data.results
    );

    // Update balance after successful operation
    if (updateBalance) {
      await updateBalance();
    }

    return response.data.data.results;
  } catch (error) {
    console.error("Error posting data", error);
    throw error;
  }
};

const get_seo_meta_description_for_multiple_keywords = async (data, onInsufficientCredits = null) => {
  try {
    // Check balance before proceeding
    const { canProceed, updateBalance } = await checkBalanceAndProceed("seo_meta_description_multiple_keywords", onInsufficientCredits);
    if (!canProceed) {
      return;
    }

    const response = await axiosInstance.post(
      "/llm/seo/meta_description/multiple_keywords",
      data
    );

    // Track the action after successful API call
    await trackAction(
      "seo_meta_description_multiple_keywords", 
      `SEO meta description for multiple keywords: ${data.keywords.join(', ')}`,
      data,
      response.data.data.results
    );

    // Update balance after successful operation
    if (updateBalance) {
      await updateBalance();
    }

    return response.data.data.results;
  } catch (error) {
    console.error("Error posting data", error);
    throw error;
  }
};

const get_seo_article_headline_for_single_keyword = async (data, onInsufficientCredits = null) => {
  try {
    // Check balance before proceeding
    const { canProceed, updateBalance } = await checkBalanceAndProceed("article_headline_single_keyword", onInsufficientCredits);
    if (!canProceed) {
      return;
    }

    const response = await axiosInstance.post(
      "/llm/seo/article_headline/single_keyword",
      data
    );

    // Track the action after successful API call
    await trackAction(
      "article_headline_single_keyword", 
      `SEO article headline for keyword: ${data.keywords}`,
      data,
      response.data.data.results
    );

    // Update balance after successful operation
    if (updateBalance) {
      await updateBalance();
    }

    return response.data.data.results;
  } catch (error) {
    console.error("Error posting data", error);
    throw error;
  }
};

const get_seo_article_headline_for_url = async (data, onInsufficientCredits = null) => {
  try {
    // Check balance before proceeding
    const { canProceed, updateBalance } = await checkBalanceAndProceed("article_headline_url", onInsufficientCredits);
    if (!canProceed) {
      return;
    }

    const response = await axiosInstance.post(
      "/llm/seo/article_headline/url",
      data
    );

    // Track the action after successful API call
    await trackAction(
      "article_headline_url", 
      `SEO article headline for URL: ${data.url}`,
      data,
      response.data.data.results
    );

    // Update balance after successful operation
    if (updateBalance) {
      await updateBalance();
    }

    return response.data.data.results;
  } catch (error) {
    console.error("Error posting data", error);
    throw error;
  }
};

const get_seo_image_alt_tags_for_single_keyword = async (data, onInsufficientCredits = null) => {
  try {
    // Check balance before proceeding
    const { canProceed, updateBalance } = await checkBalanceAndProceed("seo_image_optimisation_single_keyword", onInsufficientCredits);
    if (!canProceed) {
      return;
    }

    const response = await axiosInstance.post(
      "/llm/seo/image_alt_tags/single_keyword",
      data
    );

    // Track the action after successful API call
    await trackAction(
      "seo_image_optimisation_single_keyword", 
      `SEO image alt tags for keyword: ${data.keyword}`,
      data,
      response.data.data.results
    );

    // Update balance after successful operation
    if (updateBalance) {
      await updateBalance();
    }

    return response.data.data.results;
  } catch (error) {
    console.error("Error posting data", error);
    throw error;
  }
};

const submitKeywordAnalysisRequest = async (data, actionType, onInsufficientCredits = null) => {
  try {
    // Check balance before proceeding
    const { canProceed, updateBalance } = await checkBalanceAndProceed(actionType, onInsufficientCredits);
    if (!canProceed) {
      return;
    }
    const response = await axiosInstance.post("/search-ranking", {
      endPoint: "/search/search_summary/all_in_one_analysis_google_scraping_post",
      data: {
        keywords: data.keywords,
        domain_link: data.domainLink,
        num_results: 50,
        timeout: 5,
        enable_threads: true,
      }
    });

    // Track the action after successful API call
    await trackAction(
      actionType,
      `Keyword analysis for: ${data.keywords && Array.isArray(data.keywords) && data.keywords.length > 0 ? data.keywords.join(", ") : "N/A"}`,
      data,
      response.data.search_rank_response
      
    );

    // Update balance after successful operation
    if (updateBalance) {
      await updateBalance();
    }

    return response.data.search_rank_response;
  } catch (error) {
    console.error("Error posting data", error);
    throw error;
  }
};

const submitGoogleAdsAnalysisRequest = async (data, actionType, onInsufficientCredits = null) => {
  try {
    
    // Check balance before proceeding
    const { canProceed, updateBalance } = await checkBalanceAndProceed(actionType, onInsufficientCredits);
    if (!canProceed) {
      return;
    }
    let keywords = [];
    if(actionType === "keyword_ideas_single_keyword"){
      keywords = [data.keyword];
    }
    else if(actionType === "keyword_ideas_multiple_keywords"){
      keywords = data.keywords;
    }
    else if(actionType === "keyword_ideas_url"){
      keywords = [];
    }
    const response = await axiosInstance.post("/search-ranking", {
      endPoint: "/search/search_summary/get_google_ads_keywords",
      data: {
        keywords: keywords,
        page_url: data.pageUrl,
        location_ids: [Number(data.country)], // convert to integer
        num_keywords: 100,
        timeout: 5,
        enable_threads: true,
      }
    });

    // Track the action after successful API call
    await trackAction(
      actionType,
      `Google Ads keyword ideas for: ${data.keywords && Array.isArray(data.keywords) && data.keywords.length > 0 ? data.keywords.join(", ") : "N/A"}`,
      data,
      response.data
    );

    // Update balance after successful operation
    if (updateBalance) {
      await updateBalance();
    }
    return response.data;
  } catch (error) {
    console.error("Error posting data", error);
    throw error;
  }
};

const submitGoogleAdsKeywordAnalysisRequest = async (data, onInsufficientCredits = null) => {
  try {
    // Check balance before proceeding
    const { canProceed, updateBalance } = await checkBalanceAndProceed("google_ads_integrated_keywords", onInsufficientCredits);
    if (!canProceed) {
      return;
    }

    const response = await axiosInstance.post("/search-ranking", {
      endPoint: "/search/search_summary/get_google_ads_keywords",
      data: {
        keywords: data.keywords,
        location_ids: data.country,
        num_keywords: 3000,
        ...(data.pageUrl && { page_url: data.pageUrl }),
        ...(data.site && { site: data.site }),
      }
    });

    // Track the action after successful API call
    await trackAction(
      "google_ads_integrated_keywords",
      `Google Ads integrated keywords analysis for: ${data.keywords && Array.isArray(data.keywords) && data.keywords.length > 0 ? data.keywords.join(", ") : "N/A"}`,
      data,
      response.data
    
    );

    // Update balance after successful operation
    if (updateBalance) {
      await updateBalance();
    }

    return response.data;
  } catch (error) {
    console.error("Error posting data", error);
    throw error;
  }
};

const submitPageLevelContentOutlineRequest = async (data, actionType, onInsufficientCredits = null) => {
  try {
    // Check balance before proceeding
    const { canProceed, updateBalance } = await checkBalanceAndProceed(actionType, onInsufficientCredits);
    if (!canProceed) {
      return;
    }
    const response = await axiosInstance.post("/content-generation", {
      endPoint: "/generate_content",
      data: data
    },{ timeout: 300000 });

    // Track the action after successful API call
    await trackAction(
      actionType,
      `Content outline for: ${data.primary_keyword}`,
      data,
      response.data.output
    );

    // Update balance after successful operation
    if (updateBalance) {
      await updateBalance();
    }
    return response.data.output;
  } catch (error) {
    console.error("Error posting data", error);
    throw error;
  }
};

const submitKeywordLevelContentOutlineRequest = async (data, actionType, onInsufficientCredits = null) => {
  try {
    // Check balance before proceeding
    const { canProceed, updateBalance } = await checkBalanceAndProceed(actionType, onInsufficientCredits);
    if (!canProceed) {
      return;
    }
    const response = await axiosInstance.post("/content-generation", {
      endPoint: "/generate_title_content",
      data: data
    },{ timeout: 300000 });

    // Track the action after successful API call
    await trackAction(
      actionType,
      `Keyword analysis for: ${data.primary_keyword}`,
      data,
      response.data.output
    );

    // Update balance after successful operation
    if (updateBalance) {
      await updateBalance();
    }
    return response.data.output;
  } catch (error) {
    console.error("Error posting data", error);
    throw error;
  }
};

const submitContentOutlineDraftRequest = async (data, actionType, onInsufficientCredits = null) => {
  try {
    // Check balance before proceeding
    const { canProceed, updateBalance } = await checkBalanceAndProceed(actionType, onInsufficientCredits);
    if (!canProceed) {
      return;
    }
    const response = await axiosInstance.post("/content-generation", {
      endPoint: "/generate",
      data: data
    },{ timeout: 300000 }); 

    // Track the action after successful API call
    await trackAction(
      actionType,
      `Content outline draft for: ${data.primary_keyword}`,
      data,
      response.data.blog_content
    );

    // Update balance after successful operation
    if (updateBalance) {
      await updateBalance();
    }
    return response.data.blog_content;
  } catch (error) {
    console.error("Error posting data", error);
    throw error;
  }
};

const submitAiOverviewByKeywordRequest = async (data, actionType, onInsufficientCredits = null) => {
  try {
    // Check balance before proceeding
    const { canProceed, updateBalance } = await checkBalanceAndProceed(actionType, onInsufficientCredits);
    if (!canProceed) {
      return;
    }
    const response = await axiosInstance.post("/ai-overview", {
      endPoint: "/generate_ai_overview_by_keyword",
      data: data
    },{ timeout: 300000 });

    // Track the action after successful API call
    await trackAction(
      actionType,
      `AI overview for: ${data.keyword}`,
      data,
      response.data.output
    );
    // Update balance after successful operation
    if (updateBalance) {
      await updateBalance();
    }
    return response.data.output;
  } catch (error) {
    console.error("Error posting data", error);
    throw error;
  }
};

const submitAiOverviewByUrlRequest = async (data, actionType, onInsufficientCredits = null) => {
  try {
    // Check balance before proceeding
    const { canProceed , updateBalance } = await checkBalanceAndProceed(actionType, onInsufficientCredits);
    if (!canProceed) {
      return;
    }
    const response = await axiosInstance.post("/ai-overview", {
      endPoint: "/generate_ai_overview_by_url",
      data: data
    },{ timeout: 300000 });

    // Track the action after successful API call
    await trackAction(
      actionType,
      `AI overview for: ${data.url}`,
      data,
      response.data.output
    );
    // Update balance after successful operation
    if (updateBalance) {
      await updateBalance();
    }
    return response.data.output;
  } catch (error) {
    console.error("Error posting data", error);
    throw error;
  }
};

const submitAiOverviewByContentRequest = async (data, actionType, onInsufficientCredits = null) => {
  try {
    // Check balance before proceeding
    const { canProceed , updateBalance } = await checkBalanceAndProceed(actionType, onInsufficientCredits);
    if (!canProceed) {
      return;
    }
    const response = await axiosInstance.post("/ai-overview", {
      endPoint: "/generate_ai_overview_by_blog_text",
      data: data
    },{ timeout: 300000 });

    // Track the action after successful API call
    await trackAction(
      actionType,
      `AI overview for content: ${data.text.substring(0, 50)}...`,
      data,
      response.data.output
    );
    // Update balance after successful operation
    if (updateBalance) {
      await updateBalance();
    }
    return response.data.output;
  } catch (error) {
    console.error("Error posting data", error);
    throw error;
  }
};

const submitCopyPasteKeywordRequest = async (data, actionType, onInsufficientCredits = null) => {
  try {
    // Check balance before proceeding
    const { canProceed , updateBalance } = await checkBalanceAndProceed(actionType, onInsufficientCredits);
    if (!canProceed) {
      return;
    }
    const response = await axiosInstance.post("/content-generation", {
      endPoint: "/generate_blog_content",
      data: data
    },{ timeout: 300000 });

    // Track the action after successful API call
    await trackAction(
      actionType,
      `Copy paste content outline for: ${data.primary_keyword}`,
      data,
      response.data.output
    );
    // Update balance after successful operation
    if (updateBalance) {
      await updateBalance();
    }
    return response.data.output;
  } catch (error) {
    console.error("Error posting data", error);
    throw error;
  }
};

export {
  submitGoogleAdsKeywordAnalysisRequest,
  submitKeywordAnalysisRequest,
  submitGoogleAdsAnalysisRequest,
  submitPageLevelContentOutlineRequest,
  submitContentOutlineDraftRequest,
  submitKeywordLevelContentOutlineRequest,
  submitAiOverviewByKeywordRequest,
  submitAiOverviewByUrlRequest,
  submitAiOverviewByContentRequest,
  submitCopyPasteKeywordRequest,
  get_seo_title_for_single_keyword,
  get_seo_title_for_multiple_keywords,
  get_seo_meta_description_for_single_keyword,
  get_seo_meta_description_for_multiple_keywords,
  get_seo_article_headline_for_single_keyword,
  get_seo_article_headline_for_url,
  get_seo_image_alt_tags_for_single_keyword,
};
